import React, { useCallback, useId, useRef } from 'react';
import styles from './Tabs.module.css';

const clsx = (...args) => args.filter(Boolean).join(' ');

const TabsContext = React.createContext(null);

export function Tabs({
  value,
  onChange,
  tone = 'light',
  variant = 'underline',
  children,
  className,
}) {
  const baseId = useId();
  const getIds = useCallback(
    (tabValue) => ({
      tabId: `${baseId}-tab-${tabValue}`,
      panelId: `${baseId}-panel-${tabValue}`,
    }),
    [baseId]
  );
  return (
    <TabsContext.Provider value={{ value, onChange, tone, variant, getIds }}>
      <div className={clsx(styles.tabs, className)}>{children}</div>
    </TabsContext.Provider>
  );
}

/** `label` names the list for assistive tech where no heading labels it. */
export function TabList({ children, className, label }) {
  const ctx = React.useContext(TabsContext);
  if (!ctx) throw new Error('TabList must be used inside <Tabs>');
  const listRef = useRef(null);

  const handleKeyDown = (e) => {
    const key = e.key;
    if (
      key !== 'ArrowLeft' &&
      key !== 'ArrowRight' &&
      key !== 'Home' &&
      key !== 'End'
    ) {
      return;
    }
    const listEl = listRef.current;
    if (!listEl) return;
    const tabs = Array.from(
      listEl.querySelectorAll('[role="tab"]:not([disabled])')
    );
    if (!tabs.length) return;
    const activeEl = document.activeElement;
    const currentIndex = tabs.indexOf(activeEl);
    let nextIndex = currentIndex;
    if (key === 'ArrowLeft') {
      nextIndex = currentIndex <= 0 ? tabs.length - 1 : currentIndex - 1;
    } else if (key === 'ArrowRight') {
      nextIndex = currentIndex === tabs.length - 1 ? 0 : currentIndex + 1;
    } else if (key === 'Home') {
      nextIndex = 0;
    } else if (key === 'End') {
      nextIndex = tabs.length - 1;
    }
    if (nextIndex === currentIndex) return;
    e.preventDefault();
    const nextTab = tabs[nextIndex];
    nextTab.focus();
    const nextValue = nextTab.dataset.tabValue;
    if (nextValue !== undefined) ctx.onChange?.(nextValue);
  };

  const variantClass = styles[`tablist-${ctx.variant}`];

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={label}
      onKeyDown={handleKeyDown}
      className={clsx(
        styles.tablist,
        variantClass,
        ctx.tone === 'dark' && styles.onDark,
        className
      )}
    >
      {children}
    </div>
  );
}

export function Tab({ value, children, disabled = false, className }) {
  const ctx = React.useContext(TabsContext);
  if (!ctx) throw new Error('Tab must be used inside <Tabs>');
  const active = ctx.value === value;
  const { tabId, panelId } = ctx.getIds(value);
  return (
    <button
      type="button"
      role="tab"
      id={tabId}
      aria-selected={active}
      aria-controls={panelId}
      tabIndex={active ? 0 : -1}
      data-tab-value={value}
      disabled={disabled}
      onClick={() => !disabled && ctx.onChange?.(value)}
      className={clsx(
        styles.tab,
        styles[`tab-${ctx.variant}`],
        active && styles.active,
        ctx.tone === 'dark' && styles.onDark,
        className
      )}
    >
      {children}
    </button>
  );
}

/**
 * `keepMounted`: once shown, the panel stays mounted while another tab is
 * active — hidden, its state (a half-typed note, a chosen view, an upload in
 * flight) intact. It is still mounted only when first shown, so a panel
 * that loads something heavy loads it when it is opened, not before.
 */
export function TabPanel({ value, children, className, keepMounted = false }) {
  const ctx = React.useContext(TabsContext);
  if (!ctx) throw new Error('TabPanel must be used inside <Tabs>');
  const active = ctx.value === value;
  const [shown, setShown] = React.useState(active);
  React.useEffect(() => {
    if (active) setShown(true);
  }, [active]);
  if (!active && !(keepMounted && shown)) return null;
  const { tabId, panelId } = ctx.getIds(value);
  return (
    <div
      role="tabpanel"
      id={panelId}
      aria-labelledby={tabId}
      tabIndex={0}
      hidden={!active}
      className={clsx(styles.panel, className)}
    >
      {children}
    </div>
  );
}

export default Tabs;
